import React, { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useInView, animate } from "framer-motion";
import { ArrowUpRight, Layers, Database, TrendingUp, Server, Wrench, Map, Globe2, Radio, Zap, CheckCircle2 } from "lucide-react";
import { IMAGES, STATS, SERVICES, INDUSTRIES } from "../content";
import CoverageGlobe from "../components/CoverageGlobe";
import { Reveal, Stagger, StaggerItem, Overline, SectionShell } from "../components/Reveal";
import { Marquee } from "../components/Marquee";
import { CtaSection } from "../components/CtaSection";

const SERVICE_ICONS = [Database, Layers, TrendingUp, Server, Wrench, Map];

const Counter = ({ value, suffix }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, value, {
      duration: 1.8,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setDisplay(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, value]);
  return (
    <span ref={ref} className="font-display text-4xl sm:text-5xl font-extrabold text-white tracking-tight">
      {display.toLocaleString()}<span className="text-cyan-400">{suffix}</span>
    </span>
  );
};

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const lines = [
    { text: "THE EARTH,", cls: "text-white" },
    { text: "MACHINE-READABLE.", cls: "text-cyan-400 text-glow-cyan" },
  ];

  return (
    <section ref={ref} className="relative min-h-[100svh] flex items-center overflow-hidden noise-overlay" data-testid="home-hero">
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <img src={IMAGES.hero_earth} alt="Earth from orbit with global data networks" className="w-full h-[120%] object-cover" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-[#05070A]/55 via-[#05070A]/72 to-[#05070A]" />
      <div className="absolute inset-0 grid-overlay opacity-70" />

      <div className="absolute right-[8%] top-[18%] hidden xl:block pointer-events-none" aria-hidden="true">
        <div className="orbit-ring relative w-72 h-72 rounded-full border border-cyan-400/15">
          <div className="absolute -top-1 left-1/2 w-2 h-2 rounded-full bg-cyan-400 shadow-[0_0_14px_rgba(0,240,255,0.9)]" />
        </div>
        <div className="orbit-ring-rev absolute inset-6 rounded-full border border-blue-500/15">
          <div className="absolute top-1/2 -right-1 w-1.5 h-1.5 rounded-full bg-blue-400 shadow-[0_0_10px_rgba(37,99,235,0.9)]" />
        </div>
      </div>

      <motion.div style={{ opacity: fade }} className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 w-full pt-28 pb-20">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="flex items-center gap-3 mb-8"
        >
          <span className="relative w-2 h-2 rounded-full bg-cyan-400 ping-dot" />
          <span className="font-mono-tech text-[11px] sm:text-xs tracking-[0.3em] text-cyan-300 uppercase">
            DestinyCS — Global Geospatial Data & Engineering
          </span>
        </motion.div>

        <h1 className="font-display font-extrabold tracking-tight leading-[1.02] text-[13vw] sm:text-7xl lg:text-8xl" data-testid="hero-headline">
          {lines.map((line, i) => (
            <span key={line.text} className="mask-line">
              <motion.span
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, delay: 0.3 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
                className={line.cls}
              >
                {line.text}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.85 }}
          className="mt-7 max-w-xl text-base sm:text-lg text-slate-300 leading-relaxed"
        >
          DestinyCS turns satellite imagery, field surveys and legacy records into living
          geospatial datasets — <span className="text-white font-medium">97M+ verified POIs across 51 countries</span>,
          trusted by telecom, utilities and risk teams worldwide.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 1 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <Link
            to="/data-catalog"
            data-testid="hero-explore-catalog-button"
            className="group flex items-center gap-2 bg-cyan-400 text-[#05070A] font-semibold px-7 py-3.5 rounded-full hover:bg-cyan-300 hover:shadow-[0_0_40px_rgba(0,240,255,0.5)] transition-all duration-300"
          >
            Explore the Data Catalog
            <ArrowUpRight size={17} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </Link>
          <Link
            to="/services"
            data-testid="hero-services-button"
            className="flex items-center gap-2 border border-white/25 text-white font-medium px-7 py-3.5 rounded-full hover:border-cyan-400/60 hover:text-cyan-300 backdrop-blur-sm transition-all duration-300"
          >
            Our capabilities
          </Link>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.3 }}
          className="mt-14 flex flex-wrap gap-x-10 gap-y-3 font-mono-tech text-[11px] tracking-[0.2em] text-slate-500 uppercase"
        >
          <span>51 Countries</span><span>97M+ POI Records</span><span>4,000+ Categories</span><span>20+ Yrs Expertise</span>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
      >
        <span className="font-mono-tech text-[10px] tracking-[0.35em] text-slate-500 uppercase">Scroll</span>
        <div className="w-px h-10 bg-gradient-to-b from-cyan-400/70 to-transparent" />
      </motion.div>
    </section>
  );
};

const StatsBar = () => (
  <SectionShell id="coverage" className="py-14 sm:py-20" >
    <div data-testid="global-coverage-stats" className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 rounded-3xl overflow-hidden border border-white/10">
      {STATS.map((s, i) => (
        <Reveal key={s.label} delay={i * 0.08} className="bg-[#0B0E14]">
          <div className="p-8 sm:p-10 h-full" data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, "-")}`}>
            <Counter value={s.value} suffix={s.suffix} />
            <p className="mt-3 text-sm font-semibold text-slate-200">{s.label}</p>

          </div>
        </Reveal>
      ))}
    </div>
  </SectionShell>
);

const ServicesBento = () => (
  <SectionShell>
    <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-12">
      <div>
        <Reveal><Overline>WHAT WE DO</Overline></Reveal>
        <Reveal delay={0.08}>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white mt-3">
            The full geospatial value chain
          </h2>
        </Reveal>
      </div>
      <Reveal delay={0.15}>
        <Link to="/services" data-testid="bento-view-all-link" className="group flex items-center gap-1.5 text-cyan-400 font-medium hover:gap-2.5 transition-all">
          View all services <ArrowUpRight size={16} />
        </Link>
      </Reveal>
    </div>

    <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
      {SERVICES.map((s, i) => {
        const Icon = SERVICE_ICONS[i];
        return (
          <StaggerItem key={s.id} className={s.span === "lg" ? "lg:col-span-2" : ""}>
            <Link
              to={`/services#${s.id}`}
              data-testid={`service-card-${s.id}`}
              className="card-spotlight group relative block h-full border border-white/10 rounded-2xl bg-[#111622]/70 backdrop-blur-md p-7 sm:p-8 transition-all duration-300 hover:border-cyan-500/50 hover:shadow-[0_0_30px_rgba(0,240,255,0.12)] hover:-translate-y-1"
            >
              <div className="flex items-start justify-between">
                <div className="w-11 h-11 rounded-xl bg-cyan-400/10 border border-cyan-400/20 flex items-center justify-center">
                  <Icon size={20} className="text-cyan-400" />
                </div>
                <span className="font-mono-tech text-xs text-slate-600 group-hover:text-cyan-500 transition-colors">{s.num}</span>
              </div>
              <p className="mt-6 font-mono-tech text-[10px] tracking-[0.25em] text-slate-500">{s.tag}</p>
              <h3 className="font-display text-xl sm:text-2xl font-bold text-white mt-2 group-hover:text-cyan-300 transition-colors">{s.title}</h3>
              <p className="mt-3 text-sm text-slate-400 leading-relaxed line-clamp-3">{s.desc}</p>
              <span className="mt-5 inline-flex items-center gap-1.5 text-xs font-semibold text-cyan-400 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                Explore <ArrowUpRight size={13} />
              </span>
            </Link>
          </StaggerItem>
        );
      })}
    </Stagger>
  </SectionShell>
);

const MapShowcase = () => (
  <SectionShell>
    <div className="grid lg:grid-cols-2 gap-12 items-center">
      <div>
        <Reveal><Overline>DATA IN THE WILD</Overline></Reveal>
        <Reveal delay={0.08}>
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-white mt-3 leading-tight">
            POI coverage, quantified by country
          </h2>
        </Reveal>
        <Reveal delay={0.16}>
          <p className="mt-5 text-slate-300 leading-relaxed">
            DestinyCS boasts a comprehensive collection of place-related information worldwide.
            Each country possesses its own distinct characteristics — and our statistics reflect that
            reality honestly, coverage mapped and verified nation by nation.
          </p>
        </Reveal>
        <Stagger className="mt-8 space-y-4">
          {["51 countries with verified POI coverage", "97M+ points of interest, 31M+ in India alone", "4,000+ place categories classified", "Expanding — tell us the geography you need"].map((p) => (
            <StaggerItem key={p} className="flex items-center gap-3 text-sm text-slate-200">
              <CheckCircle2 size={17} className="text-cyan-400 shrink-0" /> {p}
            </StaggerItem>
          ))}
        </Stagger>
        <Reveal delay={0.2}>
          <Link to="/data-catalog" data-testid="coverage-catalog-button" className="mt-9 inline-flex items-center gap-2 text-cyan-400 font-semibold group">
            Browse the catalog <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </Link>
        </Reveal>
      </div>

      <Reveal delay={0.1}>
        <div className="relative" data-testid="coverage-visual">
          <CoverageGlobe />
        </div>
      </Reveal>
    </div>
  </SectionShell>
);

const IndustriesSection = () => {
  const icons = { telecom: Radio, utilities: Zap };
  return (
    <SectionShell>
      <Reveal><Overline>WHO WE SERVE</Overline></Reveal>
      <Reveal delay={0.08}>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white mt-3 mb-12">
          Industries that run on our data
        </h2>
      </Reveal>
      <Stagger className="grid md:grid-cols-2 gap-6">
        {INDUSTRIES.map((ind) => {
          const Icon = icons[ind.id] || Globe2;
          return (
            <StaggerItem key={ind.id}>
              <Link
                to={`/industries#${ind.id}`}
                data-testid={`industry-card-${ind.id}`}
                className="card-spotlight group relative block h-full border border-white/10 rounded-2xl overflow-hidden bg-[#111622]/70 hover:border-cyan-500/50 hover:-translate-y-1 transition-all duration-300"
              >
                <div className="relative h-44 overflow-hidden">
                  <img
                    src={ind.id === "telecom" ? IMAGES.city_night : IMAGES.city_lights}
                    alt={ind.title}
                    className="w-full h-full object-cover opacity-50 group-hover:opacity-70 group-hover:scale-105 transition-all duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#111622] to-transparent" />
                  <div className="absolute bottom-4 left-6 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-cyan-400/15 border border-cyan-400/25 backdrop-blur flex items-center justify-center">
                      <Icon size={18} className="text-cyan-400" />
                    </div>
                    <div>
                      <h3 className="font-display text-xl font-bold text-white">{ind.title}</h3>
                      <p className="font-mono-tech text-[10px] tracking-[0.2em] text-cyan-300/80 uppercase">{ind.subtitle}</p>
                    </div>
                  </div>
                </div>
                <div className="p-6 sm:p-7">
                  <p className="text-sm text-slate-400 leading-relaxed line-clamp-2">{ind.desc}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-cyan-400 opacity-0 group-hover:opacity-100 transition-opacity">
                    Explore solutions <ArrowUpRight size={13} />
                  </span>
                </div>
              </Link>
            </StaggerItem>
          );
        })}
      </Stagger>
    </SectionShell>
  );
};

export default function Home() {
  return (
    <main data-testid="home-page">
      <Hero />
      <Marquee />
      <StatsBar />
      <ServicesBento />
      <MapShowcase />
      <IndustriesSection />
      <CtaSection />
    </main>
  );
}
